import logo from '@Resources/assets/logos/ike-logo-dark-small.png';
import Flex from '@Resources/styles/flex.scss';
import { getBreadCrumbsString } from '@Utils/helpers/stringHelpers';
import { Button } from '@mui/material';
import cx from 'classnames';
import _isNil from 'lodash/isNil';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import IconBtnStyles from '@Components/IconButton/IconButton.scss';
import { SettingsType } from '@Components/Settings/SettingsType';
import WorkbenchSelector from '@Components/WorkbenchSelector/WorkbenchSelector';
import { isUserSuperAdminState } from '@Data/administration/Reducer';
import { selectedOrganizationState } from '@Data/organizations/Reducer';
import ProjectApi from '@Data/projects/Api';
import { selectedProjectState } from '@Data/projects/Reducer';
import { QualityControlApi } from '@Data/qualityControl/Api';
import { selectedTeamState } from '@Data/teams/Reducer';
import { WorkbenchType } from '@Data/workbenches/Interfaces';
import AuthorizationModel from '@Services/authorization/AuthorizationModel';
import { KoruRoutes } from '@Services/navigation/Paths';
import Styles from './Header.scss';
import { Dashboards } from './components/Dashboards/Dashboards';
import NotificationsComponent from './components/Notifications/NotificationsComponent';
import OrganizationSettings from './components/OrganizationSettings/OrganizationSettings';
import Search from './components/Search/Search';
import SuperAdmin from './components/SuperAdmin/SuperAdmin';
import Teams from './components/Teams/Teams';
import UserMenu from './components/UserMenu/UserMenu';

interface IHeaderProps {
    onSettingBtnClick?: (settingsType: SettingsType) => void;
    isSettingsOpen?: boolean;
    selectedSettingsType?: SettingsType;
    isSubmitting?: boolean;
}

const Header = (props: IHeaderProps) => {
    const {
        onSettingBtnClick,
        isSettingsOpen,
        selectedSettingsType,
        isSubmitting,
    } = props;

    const navigate = useNavigate();
    const selectedOrganization = useSelector(selectedOrganizationState);
    const selectedTeam = useSelector(selectedTeamState);
    const selectedProject = useSelector(selectedProjectState);
    const isUserSuperAdmin = useSelector(isUserSuperAdminState);
    const [breadCrumbsString, setBreadcrumbsString] = useState('');

    useEffect(() => {
        const breadCrumbsString = getBreadCrumbsString(
            selectedOrganization,
            selectedTeam,
            selectedProject
        );
        setBreadcrumbsString(breadCrumbsString);
    }, [selectedOrganization, selectedTeam, selectedProject]);

    const workbenchSelector = useMemo(() => {
        let output;
        if (KoruRoutes.project.matcher.test(window.location.href)) {
            output = (
                <WorkbenchSelector
                    id="header-workbench-selector"
                    initialWorkbench={WorkbenchType.QC}
                />
            );
        } else if (KoruRoutes.qc.matcher.test(window.location.href)) {
            output = (
                <WorkbenchSelector
                    id="header-workbench-selector"
                    initialWorkbench={WorkbenchType.QC}
                />
            );
        }

        return _isNil(output) ? null : (
            <div className={Styles.hChildContainer}>{output}</div>
        );
    }, [window.location.href]);

    const redirectToHome = () => {
        navigate('/');
        ProjectApi.setProject(null);
        QualityControlApi.reset();
    };

    return (
        <div id="koru-header" className={Styles.hContainer}>
            <div id="header-toolbar" className={Styles.hToolbar}>
                <div className={cx(Flex.flex, Flex.alignItemsCenter)}>
                    <div id="logo-container" className={Styles.hLogoContainer}>
                        <Button
                            id="logo-btn"
                            onClick={() => {
                                if (!isSettingsOpen) {
                                    redirectToHome();
                                }
                            }}
                        >
                            <img src={logo} />
                        </Button>
                    </div>
                    <div className={Styles.hBreadcrumbsContainer}>
                        <span
                            id={'contents-drawer-path-breadcrumbs'}
                            className={Styles.navBreadcrumbs}
                        >
                            {breadCrumbsString}
                        </span>
                    </div>
                    {!isSettingsOpen && <Search id="header-search" />}
                </div>

                <div className={Styles.hContentContainer}>
                    <div className={Styles.hContentChildrenContainer}>
                        {!isSettingsOpen && (
                            <React.Fragment>{workbenchSelector}</React.Fragment>
                        )}
                    </div>
                    <div className={Flex.flex}>
                        <Dashboards />

                        <div
                            id="header-icon-group"
                            className={IconBtnStyles.iconBtnGroup}
                        >
                            {isUserSuperAdmin && (
                                <SuperAdmin
                                    id="header-superadmin-settings-btn"
                                    onSettingsBtnClick={onSettingBtnClick}
                                    isSubmitting={isSubmitting}
                                    settingsType={selectedSettingsType}
                                />
                            )}

                            {AuthorizationModel.canViewSettingsButton(
                                SettingsType.ORGADMIN,
                                selectedOrganization?.relationship
                            ) && (
                                <OrganizationSettings
                                    id="header-organization-settings-btn"
                                    onSettingsBtnClick={onSettingBtnClick}
                                    isSubmitting={isSubmitting}
                                    settingsType={selectedSettingsType}
                                />
                            )}

                            {AuthorizationModel.canViewSettingsButton(
                                SettingsType.TEAMSADMIN,
                                selectedTeam?.relationship
                            ) && (
                                <Teams
                                    id="header-team-settings-btn"
                                    onSettingsBtnClick={onSettingBtnClick}
                                    isSubmitting={isSubmitting}
                                    settingsType={selectedSettingsType}
                                />
                            )}

                            <UserMenu
                                onSettingsBtnClick={onSettingBtnClick}
                                isSubmitting={isSubmitting}
                                settingsType={selectedSettingsType}
                            />

                            <NotificationsComponent
                                isSubmitting={isSubmitting}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Header;
